import { Box, Link } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid-pro'
import React, { FC, useContext, useMemo } from 'react'
import { generatePath, Link as RouterLink } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useGetExpandedReviewsQuery } from '../../redux/api/feedbackApi'
import { CustomerEntry, RepairOrderEntry, VehicleEntry } from '../../sdk'
import { describeVehicle } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'

const COLUMNS: GridColDef[] = [
  { field: 'datetime', headerName: 'Date', type: 'dateTime', width: 175 },
  {
    field: 'repairOrder',
    headerName: 'Repair Order',
    valueGetter: (params) => String(params.value?.localId || ''),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <Link
          component={RouterLink}
          variant="body2"
          to={generatePath(AppRoutes.ESTIMATE_DETAILS, {
            id: params.row.repairOrder.id
          })}>
          {'#' + params.value}
        </Link>
      ) : (
        <></>
      )
  },
  {
    field: 'customer',
    headerName: 'Customer',
    width: 175,
    valueGetter: (params) => formatName(params.value.information),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <Link
          component={RouterLink}
          variant="body2"
          to={generatePath(AppRoutes.CUSTOMER_DETAILS, {
            id: params.row.customer.id
          })}>
          {params.value}
        </Link>
      ) : (
        <></>
      )
  },
  {
    field: 'vehicle',
    headerName: 'Vehicle',
    width: 250,
    valueGetter: (params) => describeVehicle(params.value.info),
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <Link
          component={RouterLink}
          variant="body2"
          to={generatePath(AppRoutes.VEHICLE_DETAILS, {
            id: params.row.vehicle.id
          })}>
          {params.value}
        </Link>
      ) : (
        <></>
      )
  },
  {
    field: 'stars',
    headerName: 'Rating (1-5)',
    type: 'number',
    width: 125
  },
  {
    field: 'warranty',
    headerName: 'Warranty Service',
    type: 'boolean',
    width: 150
  },
  {
    field: 'timely',
    headerName: 'Timely Service',
    type: 'boolean',
    width: 175
  },
  {
    field: 'courteous',
    headerName: 'Courteous Staff',
    type: 'boolean',
    width: 150
  },
  {
    field: 'informed',
    headerName: 'Customer Informed',
    type: 'boolean',
    width: 175
  },
  {
    field: 'completed',
    headerName: 'Work Completed',
    type: 'boolean',
    width: 175
  }
]

interface Row {
  id: string
  datetime: Date
  repairOrder: RepairOrderEntry
  stars: number
  warranty: boolean
  timely: boolean
  courteous: boolean
  informed: boolean
  completed: boolean
  customer: CustomerEntry
  vehicle: VehicleEntry
}

const ListingByShop: FC = () => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data, isLoading } = useGetExpandedReviewsQuery({
    authorization: accessToken,
    shopId
  })

  const { gridBoxHeight } = useContext(AppContext)

  const rows: Row[] | null = useMemo(() => {
    if (!data) {
      return null
    }

    return data.map(({ feedback, repairOrder, customer, vehicle }) => ({
      id: feedback!.apiKey!,
      datetime: feedback!.dateTime!,
      repairOrder: repairOrder!,
      stars: feedback!.info!.stars!,
      warranty: feedback!.info!.warranty!,
      timely: feedback!.info!.timely!,
      courteous: feedback!.info!.courteous!,
      informed: feedback!.info!.informed!,
      completed: feedback!.info!.completed!,
      customer: customer!,
      vehicle: vehicle!
    }))
  }, [data])

  return (
    <Box sx={{ height: gridBoxHeight, width: '100%' }}>
      <DataGridPro
        columns={COLUMNS}
        rows={rows || []}
        loading={isLoading}
        density="compact"
        sx={{ border: 'none' }}
      />
    </Box>
  )
}

export default ListingByShop
